import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import {
  Divider,
  MenuItem,
  CircularProgress,
  Button,
  Typography,
} from "@mui/material";
import * as React from "react";

import { Menu } from "@/shared/components/Menu";
import { RobotoMenuItem } from "@/shared/components/RobotoMenuItem";
import { createEmptyLayout, LayoutRecord } from "@/shared/domain/layouts";

import { useActiveLayout } from "../../WorkspaceCtx/hooks";

import { LayoutMenuItem } from "./LayoutMenuItem";
import { LayoutMenuSection } from "./LayoutMenuSection";
import { useLayoutOptions } from "./useLayoutOptions";

interface SelectLayoutMenuProps {
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  onClose: () => void;
  onOpenContextMenu: (
    event: React.MouseEvent<HTMLElement>,
    layout: LayoutRecord,
  ) => void;
  onSaveLayout: () => void;
  onSelectLayout: (layout: LayoutRecord) => void;
}

export function SelectLayoutMenu(props: SelectLayoutMenuProps) {
  const {
    anchorEl,
    isOpen,
    onClose,
    onOpenContextMenu,
    onSaveLayout,
    onSelectLayout,
  } = props;

  return (
    <Menu
      id="select-layout-menu"
      anchorEl={anchorEl}
      open={isOpen}
      onClose={onClose}
      slotProps={{
        paper: {
          style: {
            /**
             * To ensure the menu doesn't overflow the viewport, we cap the
             * height at 75vh. We also cap the height at 900px to ensure the
             * menu doesn't become too tall on larger screens.
             *
             * 75vh was chosen because it leaves enough space for the header.
             * 900px was chosen through trial and error.
             */
            maxHeight: "min(900px, 75vh)",
          },
        },
      }}
    >
      <LayoutMenuItems
        onSaveLayout={onSaveLayout}
        onSelectLayout={onSelectLayout}
        onOpenContextMenu={onOpenContextMenu}
      />
    </Menu>
  );
}

function LayoutMenuItems({
  onSaveLayout,
  onSelectLayout,
  onOpenContextMenu,
}: {
  onSaveLayout: SelectLayoutMenuProps["onSaveLayout"];
  onSelectLayout: SelectLayoutMenuProps["onSelectLayout"];
  onOpenContextMenu: SelectLayoutMenuProps["onOpenContextMenu"];
}) {
  const [activeLayout] = useActiveLayout();
  const layoutOptions = useLayoutOptions();

  if (layoutOptions.isPending) {
    return (
      <MenuItem sx={{ typography: "body2", gap: 1.5 }} disableRipple>
        <CircularProgress size={16} />
        <Typography variant="body2">Loading layouts...</Typography>
      </MenuItem>
    );
  }

  if (layoutOptions.isError) {
    return (
      <MenuItem
        disableRipple
        sx={{
          typography: "body2",
          color: "error.main",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <p>Failed to load layouts</p>
        <Button
          size="small"
          variant="outlined"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            void layoutOptions.refetch();
          }}
          startIcon={<RefreshIcon />}
        >
          Try Again
        </Button>
      </MenuItem>
    );
  }

  return (
    <>
      <RobotoMenuItem
        eventName={"OpenSaveLayoutConfirmationClicked"}
        onClick={onSaveLayout}
        disableRipple
        sx={{ py: 1.2 }}
      >
        <SaveIcon fontSize="small" sx={{ color: "grey.700" }} />
        <Typography variant="body2">Save layout</Typography>
      </RobotoMenuItem>
      <RobotoMenuItem
        disableRipple
        eventName={"ClearLayoutClicked"}
        onClick={() => onSelectLayout(createEmptyLayout())}
        sx={{ py: 1.2 }}
      >
        <ClearIcon fontSize="small" sx={{ color: "grey.700" }} />
        <Typography variant="body2">Clear layout</Typography>
      </RobotoMenuItem>
      <Divider />

      <LayoutMenuSection
        title="Personal Layouts"
        hasNoLayouts={layoutOptions.personalLayouts.length === 0}
        hasNoLayoutsMessage="No personal layouts yet"
      >
        {layoutOptions.personalLayouts.map((layoutOption) => (
          <LayoutMenuItem
            key={layoutOption.id}
            isActive={activeLayout.layout_id === layoutOption.id}
            isDefault={layoutOption.isDefault}
            layout={layoutOption.record}
            onSelect={onSelectLayout}
            onOpenContextMenu={onOpenContextMenu}
          />
        ))}
      </LayoutMenuSection>

      <LayoutMenuSection
        title="Team Layouts"
        hasNoLayouts={layoutOptions.teamLayouts.length === 0}
        hasNoLayoutsMessage="No team layouts yet"
      >
        {layoutOptions.teamLayouts.map((layoutOption) => (
          <LayoutMenuItem
            key={layoutOption.id}
            isDefault={layoutOption.isDefault}
            layout={layoutOption.record}
            onSelect={onSelectLayout}
            onOpenContextMenu={onOpenContextMenu}
          />
        ))}
      </LayoutMenuSection>
    </>
  );
}
